import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
  House, ChatCircleDots, Kanban, CreditCard, Bell, Gear, User, SignOut, Books, Folder,
  Plus, ShieldCheck, TrendUp, Brain, Files,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import GlobalSearch from "@/components/GlobalSearch";

const CUSTOMER_NAV = [
  { to: "/dashboard",           label: "Overview",      icon: House,           end: true },
  { to: "/dashboard/projects",  label: "Projects",      icon: Kanban },
  { to: "/dashboard/chat",      label: "AI Assistant",  icon: ChatCircleDots },
  { to: "/dashboard/assistant", label: "Knowledge Assistant", icon: Books },
  { to: "/dashboard/knowledge-base", label: "Knowledge Base", icon: Folder },
  { to: "/dashboard/office-tools", label: "Office Tools", icon: Files },
  { to: "/dashboard/billing",   label: "Billing",       icon: CreditCard },
  { to: "/dashboard/notifications", label: "Notifications", icon: Bell },
];

const CUSTOMER_ACCOUNT = [
  { to: "/dashboard/profile",  label: "Profile",  icon: User },
  { to: "/dashboard/settings", label: "Settings", icon: Gear },
];

const ADMIN_NAV = [
  { to: "/admin",           label: "Command Center", icon: ShieldCheck, end: true },
  { to: "/admin/projects",  label: "All Projects",   icon: Kanban },
  { to: "/admin/clients",   label: "Clients",        icon: User },
  { to: "/admin/payments",   label: "Payments",       icon: CreditCard },
  { to: "/admin/analytics",  label: "Analytics",      icon: TrendUp },
  { to: "/admin/ai-usage",   label: "AI Usage",       icon: ChatCircleDots },
  { to: "/admin/audit-logs", label: "Audit Logs",     icon: Books },
  { to: "/admin/brain",      label: "Brain Lab",      icon: Brain, adminOnly: true },
  { to: "/dashboard/office-tools", label: "Office Tools", icon: Files },
];

export default function AppShell({ children, title, subtitle, actions }) {
  const { user, profile, platformRole, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  const displayName = profile?.full_name || user?.user_metadata?.full_name || user?.email || "";
  const displayEmail = user?.email || "";

  useEffect(() => {
    api.get("/notifications/unread-count").then(({ data }) => setUnread(data.count)).catch(() => {});
    const t = setInterval(() => {
      api.get("/notifications/unread-count").then(({ data }) => setUnread(data.count)).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, [location.pathname]);

  const isAdmin = platformRole === "admin" || platformRole === "staff";
  const nav = isAdmin
    ? ADMIN_NAV.filter(item => !item.adminOnly || platformRole === "admin")
    : CUSTOMER_NAV;
  const initials = (displayName || "?").split(" ").map(s => s[0]).join("").slice(0,2).toUpperCase();

  return (
    <div className="min-h-screen bg-white text-[#0B1F3A] flex">
      {/* SIDEBAR */}
      <aside className="hidden lg:flex flex-col w-60 border-r border-black/10 sticky top-0 h-screen bg-white z-20" data-testid="sidebar">
        <Link to="/" className="flex items-center gap-2 h-16 px-5 border-b border-[#DCE3ED]" data-testid="sidebar-brand">
          <img src="/agenaix/logo-primary.svg" alt="Agenaix" className="h-5 w-auto" />
        </Link>

        <div className="px-3 py-4 flex-1 overflow-y-auto">
          <div className="fx-mono-tag px-3 mb-2" style={{fontSize:'12px'}}>{isAdmin ? "ADMIN" : "WORKSPACE"}</div>
          <nav className="space-y-0.5">
            {nav.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`sidebar-link-${item.label.toLowerCase().replace(/ /g,'-')}`}
                className={({isActive}) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-[#0B1F3A] text-white" : "text-neutral-700 hover:bg-[#F4F6F8]"
                  }`
                }
              >
                <item.icon size={16} weight="duotone" />
                <span>{item.label}</span>
                {item.to === "/dashboard/notifications" && unread > 0 && (
                  <span className="ml-auto bg-[#FF3B30] text-white text-xs font-mono px-1.5 py-0.5 rounded-sm" data-testid="sidebar-unread-badge">{unread}</span>
                )}
              </NavLink>
            ))}
          </nav>

          {!isAdmin && (
            <>
              <div className="fx-mono-tag px-3 mt-6 mb-2" style={{fontSize:'12px'}}>ACCOUNT</div>
              <nav className="space-y-0.5">
                {CUSTOMER_ACCOUNT.map(item => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={`sidebar-link-${item.label.toLowerCase()}`}
                    className={({isActive}) =>
                      `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive ? "bg-[#0B1F3A] text-white" : "text-neutral-700 hover:bg-[#F4F6F8]"
                      }`
                    }
                  >
                    <item.icon size={16} weight="duotone" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </nav>
            </>
          )}
        </div>

        {!isAdmin && (
          <div className="p-3 border-t border-black/10">
            <Button onClick={() => navigate("/dashboard/new")} className="fx-btn-primary rounded-md w-full h-10" data-testid="sidebar-new-project">
              <Plus size={14} weight="bold" className="mr-2" /> New project
            </Button>
          </div>
        )}
      </aside>

      {/* MAIN */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* TOP BAR */}
        <header className="fx-glass-nav sticky top-0 z-30 h-16 flex items-center px-6 lg:px-8 gap-4" data-testid="top-bar">
          <div className="lg:hidden flex items-center gap-2">
            <img src="/agenaix/logo-primary.svg" alt="Agenaix" className="h-5 w-auto" />
          </div>

          <GlobalSearch isAdmin={isAdmin} />

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => navigate("/dashboard/notifications")}
              className="relative w-9 h-9 flex items-center justify-center rounded-md border border-black/10 hover:bg-[#F4F6F8]"
              data-testid="topbar-notifications-btn"
              aria-label="Notifications"
            >
              <Bell size={16} weight="duotone" />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#FF3B30] text-white text-xs font-mono px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{unread}</span>
              )}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger data-testid="topbar-user-menu" className="flex items-center gap-2 h-9 px-2 pr-3 rounded-md border border-black/10 hover:bg-[#F4F6F8]">
                <div className="w-6 h-6 rounded-sm bg-[#0B4DCC] text-white text-xs font-semibold flex items-center justify-center">{initials}</div>
                <span className="text-sm font-medium max-w-[120px] truncate">{displayName}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-md">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{displayName}</div>
                  <div className="text-xs text-neutral-500 font-mono truncate">{displayEmail}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {!isAdmin && (
                  <>
                    <DropdownMenuItem onSelect={() => navigate("/dashboard/profile")} data-testid="menu-profile">
                      <User size={14} className="mr-2" /> Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => navigate("/dashboard/settings")} data-testid="menu-settings">
                      <Gear size={14} className="mr-2" /> Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => navigate("/dashboard/billing")} data-testid="menu-billing">
                      <CreditCard size={14} className="mr-2" /> Billing
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onSelect={async () => { await signOut(); navigate("/"); }} data-testid="menu-logout" className="text-[#FF3B30] focus:text-[#FF3B30]">
                  <SignOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* PAGE HEADER + CONTENT */}
        <main className="flex-1 min-w-0">
          {(title || actions) && (
            <div className="px-6 lg:px-10 pt-8 pb-6 border-b border-black/10 flex flex-wrap gap-4 items-end justify-between">
              <div>
                {subtitle && <div className="fx-mono-tag mb-2">{subtitle}</div>}
                {title && <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter leading-none" data-testid="page-title">{title}</h1>}
              </div>
              {actions && <div className="flex gap-2">{actions}</div>}
            </div>
          )}
          <div className="px-6 lg:px-10 py-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
